// Frontend project service: fetch a project by id using APIs described in ProjectPage logic
import { urlMap, getAuthHeaders } from "../utils";

function trimTrailingSlash(url) {
  return (url || "").replace(/\/+$/, "");
}

export async function fetchProjectById(projectId) {
  if (!projectId) throw new Error("NO_PROJECT_ID");
  const headers = typeof getAuthHeaders === "function" ? getAuthHeaders() : {};

  // Prefer dedicated endpoint when available
  const getOne = urlMap?.getProject?.replace?.(":projectId", projectId);
  if (getOne) {
    const res = await fetch(getOne, {
      method: "GET",
      headers,
      credentials: "include",
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  }

  // Fallback: list and find (include archived by using view=all)
  const base = trimTrailingSlash(urlMap?.listProjects || "/core/projects");
  const res = await fetch(`${base}?view=all`, {
    method: "GET",
    headers,
    credentials: "include",
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const all = await res.json();
  const project = Array.isArray(all) ? all.find((p) => p.id === projectId) : null;
  if (!project) throw new Error("PROJECT_NOT_FOUND");
  return project;
}

export async function fetchProjectTypeById(projectTypeId) {
  if (!projectTypeId) throw new Error("NO_PROJECT_TYPE_ID");
  const headers = { Accept: "application/json", ...getAuthHeaders() };

  // Preferred: listProjectTypes/:id
  const fromList = urlMap?.listProjectTypes ? `${trimTrailingSlash(urlMap.listProjectTypes)}/${projectTypeId}` : null;
  const url = fromList || `/core/project-types/${projectTypeId}`;

  const res = await fetch(url, { method: "GET", headers, credentials: "include" });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`HTTP_${res.status}${text ? ` · ${text.slice(0, 120)}` : ""}`);
  }
  const ct = (res.headers.get("content-type") || "").toLowerCase();
  if (!ct.includes("application/json")) {
    const text = await res.text().catch(() => "");
    throw new Error(`EXPECTED_JSON_GOT_${ct || "unknown"}${text ? ` · ${text.slice(0, 120)}` : ""}`);
  }
  return await res.json();
}

export async function fetchChaptersByProjectId(projectId) {
  if (!projectId) throw new Error("NO_PROJECT_ID");
  const headers = { "Content-Type": "application/json", ...getAuthHeaders() };
  const url = typeof urlMap?.listChapters === "function"
    ? urlMap.listChapters(projectId)
    : `/core/projects/${projectId}/chapters`;

  const res = await fetch(url, { method: "GET", headers, credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const chapters = await res.json();
  return Array.isArray(chapters) ? chapters : [];
}

// Load project, its type, and chapters into context (where applicable) and return all.
// This centralizes the "refresh-safe" logic used by pages.
export async function loadContext(projectId, appContext) {
  if (!projectId) return { project: null, projectType: null, chapters: [] };

  const project = await fetchProjectById(projectId);
  appContext?.setCurrentProject?.(project);

  const typeId = project?.project_type_key;
  let projectType = null;
  let chapters = [];

  if (typeId) {
    const cached = appContext?.state?.projectTypes?.[typeId] || appContext?.state?.currentProjectType;
    const matches = cached && [cached.id, cached.key, cached.uuid].includes(typeId);
    // Fetch projectType (if needed) and chapters concurrently
    const [pt, ch] = await Promise.all([
      (async () => {
        if (matches) return cached;
        const fetched = await fetchProjectTypeById(typeId);
        appContext?.setProjectType?.(fetched);
        return fetched;
      })(),
      fetchChaptersByProjectId(project.id),
    ]);
    projectType = pt || null;
    chapters = Array.isArray(ch) ? ch : [];
  } else {
    // No project type assigned; still load chapters
    chapters = await fetchChaptersByProjectId(project.id).catch(() => []);
  }

  // Seed chapters into context cache for current project
  appContext?.setChapters?.(project.id, chapters);

  return { project, projectType, chapters };
}
